import {Stack, Typography} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import PreviewTag from '@shared/components/PreviewTag'

const PageTitle: FC = (): ReactElement => {
  const [title, setTitle] = useState<string>('')
  const [isIncidents, setIsIncidents] = useState(false)
  const location = useLocation()
  const [tBatchTraceability] = useCustomTranslation('batch_traceability')
  const [tEdgeApi] = useCustomTranslation('edge_api')
  const [tSettings] = useCustomTranslation('settings')
  const [tCommon] = useCustomTranslation('common')
  const [tDashboards] = useCustomTranslation('dashboards')
  const [tLogin] = useCustomTranslation('login')
  const [tLineManagement] = useCustomTranslation('line_management')
  const [tAnomalies] = useCustomTranslation('anomalies')
  const [tThresholds] = useCustomTranslation('program_signal_threshold')
  const [tIncidents] = useCustomTranslation('incidents')
  const [tTasks] = useCustomTranslation('tasks')

  useEffect(() => {
    const path = location.pathname
    const isIncidentsRoute = path.includes('/incidents')
    setIsIncidents(isIncidentsRoute)

    if (path.includes('/home') || path.includes('/lines_overview') || path === '/') {
      setTitle(tCommon('home'))
    } else if (path.includes('/line') && !path.includes('line_management')) {
      setTitle(tCommon('line_dashboard'))
    } else if (path.startsWith('/signal-explorer')) {
      setTitle(tCommon('signals_page.page_title'))
    } else if (path.includes('/batch_traceability')) {
      setTitle(tBatchTraceability('title'))
    } else if (path.includes('/dashboards')) {
      setTitle(tDashboards('dashboards'))
    } else if (path.includes('/connections')) {
      setTitle(tEdgeApi('connections'))
    } else if (path.includes('/settings')) {
      setTitle(tSettings('title'))
    } else if (path.includes('/no-organization')) {
      setTitle(tLogin('no_organization_access.page_title'))
    } else if (path.includes('/line') && path.includes('/line_management')) {
      setTitle(tLineManagement('page_title'))
    } else if (path.includes('/all-signals')) {
      setTitle(tCommon('signals.plural'))
    } else if (path.includes('/equipment_management')) {
      setTitle(tLineManagement('equipment_management.title'))
    } else if (path.includes('/equipment') && !path.includes('equipment_management')) {
      setTitle(tLineManagement('equipment_dashboard.title'))
    } else if (path.includes('/anomalies')) {
      setTitle(tAnomalies('anomalies'))
    } else if (path.includes('/logbook')) {
      setTitle(tCommon('logbook'))
    } else if (path.includes('/threshold-violations')) {
      setTitle(tThresholds('program_signal_threshold_violations'))
    } else if (path.includes('/signal-value-trackers')) {
      setTitle(tCommon('signal_value_tracker.title'))
    } else if (path.includes('/reports')) {
      setTitle(tCommon('reporting.reports_title'))
    } else if (path.includes('/tasks')) {
      setTitle(tTasks('page_title'))
    } else if (path.includes('/cockpit')) {
      setTitle('Cockpit')
    } else if (isIncidentsRoute) {
      setTitle(tIncidents('labels.incidents'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    tBatchTraceability,
    tEdgeApi,
    tSettings,
    tDashboards,
    tLogin,
    tLineManagement,
    tCommon,
    tIncidents,
    tTasks,
  ])

  if (isIncidents) {
    return (
      <Stack spacing={0.5} data-testid="mbai-main-page-title">
        <Typography
          variant="h3"
          component="h1"
          sx={{
            color: 'text.primary',
            m: 0,
            fontFamily: 'Circular Black',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        <PreviewTag />
      </Stack>
    )
  }

  return (
    <Typography
      variant="h3"
      component="h1"
      gutterBottom
      data-testid="mbai-main-page-title"
      sx={{
        color: 'text.primary',
        m: 0,
        fontFamily: 'Circular Black',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </Typography>
  )
}

export default PageTitle
